import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useAuth as useClerkAuth, SignIn, SignUp } from '@clerk/react';
import { useAuth as useAppAuth } from '../../hooks/useAuth';
import accountService from '../../services/accountService';
import { safeStorageRemove, safeStorageSet } from '../../utils/storage';

class ClerkWidgetBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(err) {
    // Keep this log for production diagnostics if Clerk widget fails at runtime.
    console.error('Clerk widget render failure:', err);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || null;
    }

    return this.props.children;
  }
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="auth-shell">
      <aside className="auth-brand-panel">
        <div className="auth-brand-content">
          <p className="auth-brand-kicker">Mololine Fleet Suite</p>
          <h1>Mololine</h1>
          <p className="auth-brand-subtitle">{subtitle}</p>
          <div className="auth-brand-badge">Secure dashboard access powered by Clerk</div>
        </div>
      </aside>

      <main className="auth-form-panel">
        <div className="auth-form-card">
          <h2>{title}</h2>
          {children}
        </div>
      </main>
    </div>
  );
}

export function SignInPage() {
  const { isLoaded, isSignedIn, signOut } = useClerkAuth();
  const { user, loading, authIssue } = useAppAuth();
  const [forcedSignOutInProgress, setForcedSignOutInProgress] = React.useState(false);
  const [signupStatus, setSignupStatus] = React.useState({
    password_signup_enabled: false,
    social_signup_enabled: true,
    signup_enabled: false,
    admin_signup_enabled: false,
    driver_signup_enabled: false,
    admin_slots_remaining: null,
    checked: false,
  });

  React.useEffect(() => {
    let active = true;

    accountService
      .getSignupStatus()
      .then((data) => {
        if (!active) return;
        setSignupStatus({
          password_signup_enabled: Boolean(data?.password_signup_enabled),
          social_signup_enabled: Boolean(data?.social_signup_enabled),
          signup_enabled: Boolean(data?.signup_enabled),
          admin_signup_enabled: Boolean(data?.admin_signup_enabled),
          driver_signup_enabled: Boolean(data?.driver_signup_enabled),
          admin_slots_remaining: data?.admin_slots_remaining,
          checked: true,
        });
      })
      .catch(() => {
        if (!active) return;
        setSignupStatus({
          password_signup_enabled: false,
          social_signup_enabled: true,
          signup_enabled: true,
          admin_signup_enabled: true,
          driver_signup_enabled: true,
          admin_slots_remaining: null,
          checked: true,
        });
      });

    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    if (!signupStatus.checked) return;
    if (!signupStatus.admin_signup_enabled && signupStatus.driver_signup_enabled) {
      safeStorageSet('mololine:signup-role-hint', 'driver');
      return;
    }
    safeStorageRemove('mololine:signup-role-hint');
  }, [signupStatus]);

  React.useEffect(() => {
    if (!isLoaded || !isSignedIn || user) return;
    if (authIssue !== 'admin_signup_limit_reached') return;

    let active = true;
    setForcedSignOutInProgress(true);

    signOut({ redirectUrl: '/sign-up?reason=admin_signup_limit_reached' })
      .catch(() => {
        if (!active) return;
        setForcedSignOutInProgress(false);
      });

    return () => {
      active = false;
    };
  }, [isLoaded, isSignedIn, user, authIssue, signOut]);

  if (!isLoaded || loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--brand-off-white)' }}>
        <p style={{ color: 'var(--gray-600)' }}>Loading authentication…</p>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isSignedIn && !user) {
    if (authIssue === 'admin_signup_limit_reached') {
      return (
        <AuthShell
          title="Admin Sign-Up Limit Reached"
          subtitle="This account cannot be onboarded as an owner/admin right now."
        >
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <p style={{ margin: 0, color: 'var(--gray-600)' }}>
              Redirecting you to sign-up with a clear notice.
            </p>
            <button className="btn btn-primary" disabled>
              {forcedSignOutInProgress ? 'Signing out...' : 'Redirecting...'}
            </button>
          </div>
        </AuthShell>
      );
    }

    return (
      <AuthShell
        title="Finishing sign-in"
        subtitle="Your Clerk session is active. We are syncing your account details."
      >
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <p style={{ margin: 0, color: 'var(--gray-600)' }}>
            If this takes too long, you can retry sign-in.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => signOut({ redirectUrl: '/sign-in' })}
          >
            Retry Sign-In
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Sign in"
      subtitle="Track trips, monitor expenses, and run fleet operations from one control center."
    >
      <div style={{ display: 'grid', gap: '0.8rem' }}>
        <ClerkWidgetBoundary
          fallback={(
            <div style={{
              border: '1px solid var(--gray-200)',
              borderRadius: 'var(--radius-md)',
              padding: '0.85rem 0.95rem',
              fontSize: '0.86rem',
              color: 'var(--danger)',
              background: 'var(--danger-light, #fef2f2)',
            }}>
              Sign-in module failed to load. Refresh the page and try again.
            </div>
          )}
        >
          <SignIn
            routing="path"
            path="/sign-in"
            signUpUrl={signupStatus.checked && signupStatus.signup_enabled && signupStatus.social_signup_enabled ? '/sign-up' : undefined}
            forceRedirectUrl="/dashboard"
          />
        </ClerkWidgetBoundary>

        {!signupStatus.checked ? (
          <div style={{
            border: '1px solid var(--gray-200)',
            borderRadius: 'var(--radius-md)',
            padding: '0.75rem 0.9rem',
            fontSize: '0.86rem',
            color: 'var(--gray-500)',
            background: 'var(--gray-50)',
          }}>
            Checking sign-up availability...
          </div>
        ) : signupStatus.signup_enabled && signupStatus.social_signup_enabled ? (
          <div style={{
            border: '1px solid var(--gray-200)',
            borderRadius: 'var(--radius-md)',
            padding: '0.75rem 0.9rem',
            fontSize: '0.86rem',
            color: 'var(--gray-600)',
            background: 'var(--gray-50)',
          }}>
            Sign-up is available with Google social authentication.
          </div>
        ) : (
          <div style={{
            border: '1px solid var(--gray-200)',
            borderRadius: 'var(--radius-md)',
            padding: '0.75rem 0.9rem',
            fontSize: '0.86rem',
            color: 'var(--warning-text)',
            background: 'var(--warning-light)',
          }}>
            Sign-up is currently unavailable. Please contact the administrator for support.
          </div>
        )}
      </div>
    </AuthShell>
  );
}

export function SignUpPage() {
  const location = useLocation();
  const { isLoaded } = useClerkAuth();
  const { user, loading } = useAppAuth();
  const [signupStatus, setSignupStatus] = React.useState({
    password_signup_enabled: false,
    social_signup_enabled: true,
    signup_enabled: true,
    admin_signup_enabled: true,
    driver_signup_enabled: true,
    admin_slots_remaining: null,
    checked: false,
  });
  const [roleSelection, setRoleSelection] = React.useState('owner');
  const signupReason = new URLSearchParams(location.search).get('reason');
  const roleFromQuery = new URLSearchParams(location.search).get('role');
  const showAdminCapNotice = signupReason === 'admin_signup_limit_reached';
  const ownerEnabled = signupStatus.admin_signup_enabled;
  const driverEnabled = signupStatus.driver_signup_enabled;
  const selectedRole =
    roleSelection === 'driver'
      ? 'driver'
      : ownerEnabled
        ? 'owner'
        : 'driver';

  React.useEffect(() => {
    if (roleFromQuery !== 'owner' && roleFromQuery !== 'driver') return;
    setRoleSelection(roleFromQuery);
  }, [roleFromQuery]);

  React.useEffect(() => {
    let active = true;

    accountService
      .getSignupStatus()
      .then((data) => {
        if (!active) return;
        setSignupStatus({
          password_signup_enabled: Boolean(data?.password_signup_enabled),
          social_signup_enabled: Boolean(data?.social_signup_enabled),
          signup_enabled: Boolean(data?.signup_enabled),
          admin_signup_enabled: Boolean(data?.admin_signup_enabled),
          driver_signup_enabled: Boolean(data?.driver_signup_enabled),
          admin_slots_remaining: data?.admin_slots_remaining,
          checked: true,
        });
      })
      .catch(() => {
        if (!active) return;
        setSignupStatus({
          password_signup_enabled: false,
          social_signup_enabled: true,
          signup_enabled: true,
          admin_signup_enabled: true,
          driver_signup_enabled: true,
          admin_slots_remaining: null,
          checked: true,
        });
      });

    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    if (!signupStatus.checked) return;
    safeStorageSet('mololine:signup-role-hint', selectedRole);
  }, [signupStatus.checked, selectedRole]);

  if (!isLoaded || loading || !signupStatus.checked) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--brand-off-white)' }}>
        <p style={{ color: 'var(--gray-600)' }}>Loading authentication…</p>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!signupStatus.signup_enabled || !signupStatus.social_signup_enabled) {
    return (
      <AuthShell
        title="Sign-up unavailable"
        subtitle="New account registration is temporarily closed."
      >
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <p style={{ margin: 0, color: 'var(--gray-600)' }}>
            Please contact the administrator for support.
          </p>
          <Link to="/sign-in" className="btn btn-primary" style={{ justifyContent: 'center' }}>
            Go to Sign In
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Create account"
      subtitle="Continue with Google to create your Mololine account."
    >
      <div style={{ display: 'grid', gap: '0.8rem' }}>
        {!signupStatus.password_signup_enabled && (
          <div style={{
            border: '1px solid var(--gray-200)',
            borderRadius: 'var(--radius-md)',
            padding: '0.75rem 0.9rem',
            fontSize: '0.86rem',
            color: 'var(--gray-600)',
            background: 'var(--gray-50)',
          }}>
            Password sign-up is disabled. Use Continue with Google.
          </div>
        )}

        {showAdminCapNotice && (
          <div style={{
            border: '1px solid var(--warning-border, #f59e0b)',
            borderRadius: 'var(--radius-md)',
            padding: '0.75rem 0.9rem',
            fontSize: '0.86rem',
            color: 'var(--warning-text)',
            background: 'var(--warning-light)',
          }}>
            Owner sign-up limit has been reached. Please choose Driver account type or contact support.
          </div>
        )}

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Account Type</label>
          <select
            className="form-control"
            value={selectedRole}
            onChange={(e) => setRoleSelection(e.target.value)}
            disabled={!ownerEnabled || !driverEnabled}
          >
            <option value="owner" disabled={!ownerEnabled}>Fleet Owner (Admin)</option>
            <option value="driver" disabled={!driverEnabled}>Driver</option>
          </select>
          {!ownerEnabled && driverEnabled && (
            <span className="form-hint">Owner sign-up limit reached. Driver sign-up is still open.</span>
          )}
          {ownerEnabled && signupStatus.admin_slots_remaining !== null && (
            <span className="form-hint">Remaining owner slots: {signupStatus.admin_slots_remaining}</span>
          )}
        </div>

        <ClerkWidgetBoundary
          fallback={(
            <div style={{
              border: '1px solid var(--gray-200)',
              borderRadius: 'var(--radius-md)',
              padding: '0.85rem 0.95rem',
              fontSize: '0.86rem',
              color: 'var(--danger)',
              background: 'var(--danger-light, #fef2f2)',
              display: 'grid',
              gap: '0.65rem',
            }}>
              <span>Sign-up module failed to load. Try again or go back to sign-in.</span>
              <Link to="/sign-in" className="btn btn-secondary" style={{ justifyContent: 'center' }}>
                Back to Sign In
              </Link>
            </div>
          )}
        >
          <SignUp
            routing="path"
            path="/sign-up"
            signInUrl="/sign-in"
            forceRedirectUrl="/dashboard"
          />
        </ClerkWidgetBoundary>
      </div>
    </AuthShell>
  );
}
